import { Card, CardContent, Paper, Typography } from "@mui/material";
import { useFormik } from "formik";
import { PhoneNumberUtil } from "google-libphonenumber";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import {
	usePatchMyProfessionalInformationFreelancerOnboardingStep,
	useSaveMyProfessionalInformationFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import {
	useDocumentTypes,
	useJobLocations,
	useMissionsDurations,
	useUrlTypes
} from "../../../hooks/useReferential";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { FreelancerProfileCompanyUrl } from "../../../models/FreelancerProfileCompanyUrl";
import { FreelancerProfileDocument } from "../../../models/FreelancerProfileDocument";
import { FreelancerProfileUrl } from "../../../models/FreelancerProfileUrl";
import { convertFileToBase64 } from "../../../utils/file";

import {
	AddressViewModel,
	FreelancerOnboardingMyProfessionalInformationStepViewModel
} from "../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";

import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import i18n from "../../../i18n/i18n";
import { FreelancerProfileCompany } from "../../../models/FreelancerProfileCompany";
import ActionButtonsSection from "./components/professional-information/ActionButtonsSection";
import CompanyInfoSection from "./components/professional-information/CompanyInfoSection";
import DailyRateSection from "./components/professional-information/DailyRateSection";
import DocumentsAndUrlsSection from "./components/professional-information/DocumentsAndUrlsSection";
import ErrorDialog from "./components/professional-information/ErrorDialog";
import MissionPreferencesSection from "./components/professional-information/MissionPreferencesSection";
import StatusSelectionSection from "./components/professional-information/StatusSelectionSection";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";

const FreelancerOnboardingMyProfessionalInformation: React.FC = () => {
	const {
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		setFreelancerOnboardingMyProfessionalInformationStepViewModel,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancerProfile } = freelancerProfileQueryResult;
	const patchMyProfessionalInformationFreelancerOnboardingStep =
		usePatchMyProfessionalInformationFreelancerOnboardingStep();
	const saveMyProfessionalInformationFreelancerOnboardingProgress =
		useSaveMyProfessionalInformationFreelancerOnboardingProgress({
			hideAllSnackbars: true
		});
	const { t } = useTranslation();

	// Reference data hooks
	const { data: missionsDurations = [] } = useMissionsDurations();
	const { data: documentTypes = [] } = useDocumentTypes();
	const { data: urlTypes = [] } = useUrlTypes();
	const { data: jobLocations } = useJobLocations();

	// State for managing document and URL adding
	const [isAddingDocument, setIsAddingDocument] = useState(false);
	const [isAddingUrl, setIsAddingUrl] = useState(false);
	const [addingDocument, setAddingDocument] = useState<FreelancerProfileDocument | null>(
		null
	);
	const [addingUrl, setAddingUrl] = useState<FreelancerProfileUrl | null>(null);
	const [activeTable, setActiveTable] = useState<"documents" | "urls" | null>(null);
	const [
		isCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated,
		setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated
	] = useState(true);

	// State for company URL management
	const [isAddingCompanyUrl, setIsAddingCompanyUrl] = useState(false);
	const [addingCompanyUrl, setAddingCompanyUrl] =
		useState<FreelancerProfileCompanyUrl | null>(null);
	const [activeCompanyUrlTable, setActiveCompanyUrlTable] = useState(false);

	const [errorDialog, setErrorDialog] = useState({
		open: false,
		title: "",
		message: ""
	});
	const { interceptNavigation } = useNavigationInterceptorContext();

	// Individual refs for form fields that can have errors
	const dailyRateRef = useRef<HTMLSpanElement>(null);
	const missionDurationRef = useRef<HTMLInputElement>(null);
	const locationPreferenceRef = useRef<HTMLInputElement>(null);
	const statusSelectionRef = useRef<HTMLButtonElement>(null);
	const companyInfoRef = useRef<HTMLInputElement>(null);

	// Phone validation function using google-libphonenumber
	const phoneUtil = PhoneNumberUtil.getInstance();
	const isPhoneValid = (phone: string) => {
		// Handle empty or null phone numbers
		if (!phone || phone.trim() === "" || (phone.startsWith("+") && phone.length <= 4)) {
			return true; // Empty phone is valid since it's optional
		}

		try {
			return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
		} catch (error) {
			console.error("Phone validation error:", error);
			return false;
		}
	};

	// Helper function to check if last step is superior to professional information step
	const isLastStepSuperiorToProfessionalInfo = useCallback(() => {
		return (
			freelancerProfile?.onboarding?.formsProgress?.lastReachedStep &&
			freelancerProfile.onboarding.formsProgress.lastReachedStep >
				FreelancerOnboardingFormStep.MyProfessionalInformation
		);
	}, [freelancerProfile?.onboarding?.formsProgress?.lastReachedStep]);

	// Helper function to extract required fields from Yup validation schema
	const extractRequiredFieldsFromSchema = useCallback((schema: any) => {
		const requiredFields: string[] = [];

		if (schema?.fields) {
			Object.keys(schema.fields).forEach(fieldName => {
				const field = schema.fields[fieldName];

				if (!field?.spec?.nullable && !field?.spec?.optional) {
					requiredFields.push(fieldName);
				}
			});
		}

		return requiredFields;
	}, []);

	// Helper function to check if all required company fields are filled dynamically
	const areAllCompanyRequiredFieldsFilled = useCallback(
		(statusData: any) => {
			if (!statusData) return false;

			// Get the validation schema for status data
			const statusSchema = createStatusDataValidationSchema();
			const requiredFields = extractRequiredFieldsFromSchema(statusSchema);

			return requiredFields.every(field => {
				if (field === "areCompanyInformationAcknowledged") return true;

				const value = statusData[field];

				// Special handling for object fields like selectedCountry and selectedAddress
				if (field === "selectedCountry" || field === "selectedAddress") {
					return !!value;
				}

				// For string fields, check if they have meaningful content
				return value && value.toString().trim() !== "";
			});
		},
		[extractRequiredFieldsFromSchema]
	);

	// Common validation schema for both portage and structure status data.
	// When `isRegistrationInProgress` is checked, required fields are skipped:
	// only formats (email / phone) are validated when those fields are filled.
	const createStatusDataValidationSchema = () => {
		return Yup.object().shape({
			isRegistrationInProgress: Yup.boolean(),
			selectedCountry: Yup.object()
				.nullable()
				.when("isRegistrationInProgress", {
					is: true,
					then: schema => schema.nullable().notRequired(),
					otherwise: schema =>
						schema.required(
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.countryRequired"
							)
						)
				}),
			selectedAddress: Yup.object()
				.nullable()
				.when(["isRegistrationInProgress", "selectedCountry"], {
					is: (inProgress: boolean) => inProgress === true,
					then: schema => schema.nullable().notRequired(),
					otherwise: schema =>
						schema.when("selectedCountry", {
							is: (selectedCountry: any) => !selectedCountry,
							then: () =>
								Yup.object()
									.notRequired()
									.test(
										"country-required-for-address",
										t(
											"freelancerOnboardingMyProfessionalInformation.validation.selectCountryFirst"
										),
										() => false
									),
							otherwise: () =>
								Yup.object().required(
									t(
										"freelancerOnboardingMyProfessionalInformation.validation.addressRequired"
									)
								)
						})
				}),
			identificationNumber: Yup.string().when("isRegistrationInProgress", {
				is: true,
				then: schema => schema.notRequired(),
				otherwise: schema =>
					schema.required(
						t(
							"freelancerOnboardingMyProfessionalInformation.validation.identificationNumberRequired"
						)
					)
			}),
			companyName: Yup.string().when("isRegistrationInProgress", {
				is: true,
				then: schema =>
					schema
						.notRequired()
						.test(
							"company-name-min-length-when-filled",
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.companyNameMinLength"
							),
							value => !value || value.trim().length === 0 || value.trim().length >= 2
						),
				otherwise: schema =>
					schema
						.required(
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.companyNameRequired"
							)
						)
						.min(
							2,
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.companyNameMinLength"
							)
						)
			}),
			firstName: Yup.string().when("isRegistrationInProgress", {
				is: true,
				then: schema => schema.notRequired(),
				otherwise: schema =>
					schema
						.required(
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.firstNameRequired"
							)
						)
						.test(
							"is-not-empty-firstName",
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.firstNameRequired"
							),
							function (value) {
								if (!value || value.trim() === "") {
									return false;
								}

								return true;
							}
						)
			}),
			lastName: Yup.string().when("isRegistrationInProgress", {
				is: true,
				then: schema => schema.notRequired(),
				otherwise: schema =>
					schema
						.required(
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.lastNameRequired"
							)
						)
						.test(
							"is-not-empty-lastName",
							t(
								"freelancerOnboardingMyProfessionalInformation.validation.lastNameRequired"
							),
							function (value) {
								if (!value || value.trim() === "") {
									return false;
								}

								return true;
							}
						)
			}),
			email: Yup.string().when("isRegistrationInProgress", {
				is: true,
				then: schema =>
					schema
						.notRequired()
						.test(
							"email-format-when-filled",
							t("freelancerOnboardingMyProfessionalInformation.validation.emailInvalid"),
							value =>
								!value ||
								/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
						),
				otherwise: schema =>
					schema
						.required(
							t("freelancerOnboardingMyProfessionalInformation.validation.emailRequired")
						)
						.matches(
							/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
							t("freelancerOnboardingMyProfessionalInformation.validation.emailInvalid")
						)
			}),
			areCompanyInformationAcknowledged: Yup.boolean().when(
				"isRegistrationInProgress",
				{
					is: true,
					then: schema => schema.notRequired(),
					otherwise: schema =>
						schema.required().test("is-true", "", value => value === true)
				}
			),
			phoneNumber: Yup.string()
				.nullable()
				.test(
					"is-valid-phone",
					t(
						"freelancerOnboardingMyProfessionalInformation.validation.phoneNumberInvalid"
					),
					function (value) {
						if (!value || value.trim() === "") {
							return true;
						}
						return isPhoneValid(value);
					}
				)
		});
	};

	// Helper function to create status data from freelancer profile
	const createStatusDataFromProfile = useCallback(
		(company?: FreelancerProfileCompany | null) => {
			const baseData = {
				selectedCountry: null,
				selectedAddress: null,
				identificationNumber: "",
				companyName: "",
				vatNumber: "",
				firstName: "",
				lastName: "",
				email: "",
				phoneNumber: "",
				companyUrls: [],
				areCompanyInformationAcknowledged: false,
				isRegistrationInProgress: false
			};

			if (!company) {
				return {
					...baseData,
					areCompanyInformationAcknowledged: false,
					isRegistrationInProgress: false
				};
			}

			return {
				selectedCountry:
					company.countryId && company.country
						? {
								place_id: company.countryId,
								formatted_address: company.country,
								address_components: company.address?.countryCode
									? [
											{
												long_name: company.country,
												short_name: company.address.countryCode,
												types: ["country", "political"]
											}
									  ]
									: undefined
						  }
						: null,
				selectedAddress: company.address
					? {
							id: company.address.id,
							fullAddress: company.address.fullAddress,
							street: company.address.street,
							streetNumber: company.address.streetNumber,
							postalCode: company.address.postalCode,
							city: company.address.city,
							region: company.address.region,
							country: company.address.country,
							countryCode: company.address.countryCode,
							latitude: company.address.latitude,
							longitude: company.address.longitude,
							placeId: company.address.placeId,
							formattedAddress: company.address.formattedAddress
					  }
					: null,
				identificationNumber: company.identificationNumber ?? "",
				companyName: company.legalName ?? "",
				vatNumber: company.vatNumber ?? "",
				firstName: company.contactFirstName ?? "",
				lastName: company.contactLastName ?? "",
				email: company.contactEmail ?? "",
				phoneNumber: company.contactPhoneNumber ?? "",
				companyUrls:
					company.urls?.map((url: any) => ({
						id: url.id,
						url: url.url,
						type: url.type ? { id: url.type.id, label: url.type.label } : null,
						freelancerProfileCompanyId: url.freelancerProfileCompanyId
					})) ?? [],
				areCompanyInformationAcknowledged: company.areCompanyInformationAcknowledged,
				isRegistrationInProgress: company.isRegistrationInProgress === true
			};
		},
		[areAllCompanyRequiredFieldsFilled, isLastStepSuperiorToProfessionalInfo]
	);

	const formValidation = Yup.object({
		dailyRateMin: Yup.number().min(
			50,
			t("freelancerOnboardingMyProfessionalInformation.validation.minDailyRate")
		),
		dailyRateMax: Yup.number().when("dailyRateMin", (dailyRateMin: any, schema: any) => {
			return schema.test(
				"max-greater-than-min",
				t("freelancerOnboardingMyProfessionalInformation.validation.maxGreaterThanMin"),
				function (value: number) {
					const minRate = Array.isArray(dailyRateMin) ? dailyRateMin[0] : dailyRateMin;
					if (minRate && value && value <= minRate) {
						return false;
					}
					return true;
				}
			);
		}),
		preferredMissionDuration: Yup.object().required(
			t(
				"freelancerOnboardingMyProfessionalInformation.validation.missionDurationRequired"
			)
		),
		preferredLocation: Yup.object().required(
			t(
				"freelancerOnboardingMyProfessionalInformation.validation.locationPreferenceRequired"
			)
		),

		// Required fields
		selectedStatus: Yup.string()
			.required(
				t("freelancerOnboardingMyProfessionalInformation.validation.statusRequired")
			)
			.oneOf(
				["portage", "structure"],
				t("freelancerOnboardingMyProfessionalInformation.validation.statusInvalid")
			),

		// Validate status-specific data based on selected status
		portageData: Yup.object().when("selectedStatus", {
			is: "portage",
			then: () => createStatusDataValidationSchema(),
			otherwise: schema => schema.nullable()
		}),

		structureData: Yup.object().when("selectedStatus", {
			is: "structure",
			then: () => createStatusDataValidationSchema(),
			otherwise: schema => schema.nullable()
		})
	});

	const initialValues: FreelancerOnboardingMyProfessionalInformationStepViewModel = {
		dailyRateMin: freelancerProfile?.dailyRateMin ?? 50,
		dailyRateMax: freelancerProfile?.dailyRateMax ?? 2000,
		preferredMissionDuration: freelancerProfile?.missionsPreferredDuration
			? {
					id: freelancerProfile.missionsPreferredDuration.id,
					label: freelancerProfile.missionsPreferredDuration.label
			  }
			: null,
		preferredLocation: freelancerProfile?.jobPreferredLocation
			? {
					id: freelancerProfile.jobPreferredLocation.id,
					label: freelancerProfile.jobPreferredLocation.label
			  }
			: null,
		selectedStatus: (() => {
			if (!freelancerProfile?.company) return null;
			return freelancerProfile.company.isPortageSalarial ? "portage" : "structure";
		})(),

		// Initialize separate data storage for each status type
		portageData: freelancerProfile?.company?.isPortageSalarial
			? createStatusDataFromProfile(freelancerProfile.company)
			: createStatusDataFromProfile(null),

		structureData:
			freelancerProfile?.company && !freelancerProfile.company.isPortageSalarial
				? createStatusDataFromProfile(freelancerProfile.company)
				: createStatusDataFromProfile(null),

		documents:
			freelancerProfile?.documents?.map(doc => ({
				id: doc.id,
				name: doc.name,
				urlPath: doc.urlPath,
				type: doc.type ? { id: doc.type.id, label: doc.type.label } : null,
				content: doc.content,
				freelancerProfileId: doc.freelancerProfileId
			})) ?? [],
		urls:
			freelancerProfile?.urls?.map(url => ({
				id: url.id,
				url: url.url,
				type: url.type ? { id: url.type.id, label: url.type.label } : null,
				freelancerProfileId: url.freelancerProfileId
			})) ?? [],
		form: null
	};

	const formik = useFormik<FreelancerOnboardingMyProfessionalInformationStepViewModel>({
		initialValues,
		validationSchema: formValidation,
		onSubmit: async values => {
			try {
				await patchMyProfessionalInformationFreelancerOnboardingStep.mutateAsync({
					freelancerOnboardingMyProfessionalInformationStepViewModel: values,
					freelancerProfile: freelancerProfile!,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MyProfessionalInformation
							: FreelancerOnboardingFormStep.MyPersonalInformation
				});

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(
						FreelancerOnboardingFormStep.MyPersonalInformation
					);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		},
		enableReinitialize: true
	});

	// Helper functions for error scrolling
	const getFieldPriority = useCallback(() => {
		return [
			"selectedStatus",
			"dailyRateMin",
			"dailyRateMax",
			"preferredMissionDuration",
			"preferredLocation",
			"portageData",
			"structureData"
		];
	}, []);

	const findElementForField = useCallback(
		(fieldName: string, errorObj: any): HTMLElement | null => {
			// Handle nested errors (portageData/structureData)
			if (
				(fieldName === "portageData" || fieldName === "structureData") &&
				errorObj &&
				typeof errorObj === "object"
			) {
				const nestedErrorFields = Object.keys(errorObj);
				if (nestedErrorFields.length === 0) return null;

				const firstErrorField = nestedErrorFields[0];
				const selectors = [
					`input[name*="Data.${firstErrorField}"]`,
					`input[name="${fieldName}.${firstErrorField}"]`,
					`input[name*="${firstErrorField}"]`,
					`textarea[name*="Data.${firstErrorField}"]`,
					`select[name*="Data.${firstErrorField}"]`
				];

				// Add special selectors for common field types
				if (firstErrorField.toLowerCase().includes("country")) {
					selectors.push(
						'input[placeholder*="country" i]',
						'input[aria-label*="country" i]'
					);
				}
				if (firstErrorField.toLowerCase().includes("address")) {
					selectors.push(
						'input[placeholder*="address" i]',
						'input[aria-label*="address" i]'
					);
				}

				for (const selector of selectors) {
					const element = document.querySelector(selector) as HTMLElement;
					if (element) return element;
				}
				return null;
			}

			// Handle direct field errors using refs
			const fieldRefMap: { [key: string]: React.RefObject<HTMLElement> } = {
				dailyRateMin: dailyRateRef,
				dailyRateMax: dailyRateRef,
				preferredMissionDuration: missionDurationRef,
				preferredLocation: locationPreferenceRef,
				selectedStatus: statusSelectionRef
			};

			return fieldRefMap[fieldName]?.current || null;
		},
		[dailyRateRef, missionDurationRef, locationPreferenceRef, statusSelectionRef]
	);

	const preserveErrorStates = useCallback(
		(errors: any) => {
			Object.keys(errors).forEach(errorField => {
				formik.setFieldTouched(errorField, true, false);

				// Handle nested errors
				if (typeof errors[errorField] === "object" && errors[errorField] !== null) {
					Object.keys(errors[errorField]).forEach(nestedField => {
						formik.setFieldTouched(`${errorField}.${nestedField}`, true, false);
					});
				}
			});
		},
		[formik]
	);

	const scrollAndFocusElement = useCallback(
		(element: HTMLElement, errors: any) => {
			// Scroll to element
			const elementRect = element.getBoundingClientRect();
			const scrollTop = elementRect.top + window.pageYOffset - 300;

			window.scrollTo({
				top: scrollTop,
				behavior: "smooth"
			});

			// Focus after scroll completes
			preserveErrorStates(errors);
			setTimeout(() => {
				try {
					element.focus();

					// Select text for input elements
					if (element instanceof HTMLInputElement) {
						setTimeout(() => {
							preserveErrorStates(errors);
							element.select();
						}, 50);
					}
				} catch (error) {
					console.warn("Could not focus element:", error);
				}
			}, 500);
		},
		[preserveErrorStates]
	);

	const scrollToFirstError = useCallback(
		(validationErrors: any = null) => {
			const errors = validationErrors || formik.errors;
			const errorFields = Object.keys(errors);

			if (errorFields.length === 0) return;

			const fieldPriority = getFieldPriority();

			// Find the first priority field with an error
			let targetField = fieldPriority.find(field => {
				return (
					errorFields.includes(field) ||
					errorFields.some(errorField => errorField.startsWith(field + "."))
				);
			});

			// Fallback to first error field if no priority field found
			if (!targetField) {
				targetField = errorFields[0];
				if (targetField.includes(".")) {
					targetField = targetField.split(".")[0];
				}
			}

			if (!targetField) return;

			const element = findElementForField(targetField, errors[targetField]);

			if (element) {
				scrollAndFocusElement(element, errors);
			}
		},
		[formik.errors, getFieldPriority, findElementForField, scrollAndFocusElement]
	);

	const handleFormSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			const errors = await formik.validateForm();

			if (Object.keys(errors).length > 0) {
				scrollToFirstError(errors);
				return;
			}

			formik.handleSubmit(e);
		},
		[formik, scrollToFirstError]
	);

	useEffect(() => {
		setFreelancerOnboardingMyProfessionalInformationStepViewModel({
			...formik.values,
			form: {
				isValid: formValidation.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	// Auto-uncheck "registration in progress" as soon as every administrative
	// and contact required field is filled while the flag is still on. This
	// reveals the acknowledgement checkbox so the talent can finalise. The
	// `disabled` rule in CompanyInfoSection then prevents re-checking once
	// the data is complete.
	useEffect(() => {
		const status = formik.values.selectedStatus;
		if (!status) return;

		const statusData =
			status === "portage" ? formik.values.portageData : formik.values.structureData;
		if (!statusData?.isRegistrationInProgress) return;

		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		const companyName = statusData.companyName?.trim() ?? "";
		const email = statusData.email?.trim() ?? "";

		const allRequiredFilled =
			!!statusData.selectedCountry &&
			!!statusData.selectedAddress &&
			!!statusData.identificationNumber?.trim() &&
			companyName.length >= 2 &&
			!!statusData.firstName?.trim() &&
			!!statusData.lastName?.trim() &&
			!!email &&
			emailRegex.test(email);

		if (allRequiredFilled) {
			formik.setFieldValue(`${status}Data.isRegistrationInProgress`, false, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.portageData, formik.values.structureData, formik.values.selectedStatus]);


	useEffect(() => {
		setTimeout(() => {
			setFreelancerOnboardingMyProfessionalInformationStepViewModel({
				...formik.values,
				form: {
					isValid: formValidation.isValidSync(formik.values),
					isSubmitting: formik.isSubmitting,
					isDirty: !isCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated
				}
			});

			const currentStatusData = getCurrentStatusData(formik.values.selectedStatus!);
			const allRequiredFieldsFilled =
				areAllCompanyRequiredFieldsFilled(currentStatusData);

			const lastStepSuperior = isLastStepSuperiorToProfessionalInfo();

			formik.setFieldValue(
				`${formik.values.selectedStatus}Data.areCompanyInformationAcknowledged`,
				currentStatusData?.areCompanyInformationAcknowledged &&
					allRequiredFieldsFilled &&
					lastStepSuperior
			);
		}, 600);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated, i18n.language]);

	// Helper function to get current status data
	const getCurrentStatusData = useCallback(
		(status: "portage" | "structure") => {
			return status === "portage"
				? formik.values.portageData
				: formik.values.structureData;
		},
		[formik.values.portageData, formik.values.structureData]
	);

	// Helper function to update status data
	const updateStatusData = useCallback(
		(status: "portage" | "structure", data: any) => {
			const currentData = getCurrentStatusData(status);
			const mergedData = {
				...currentData,
				...data
			};

			formik.setFieldValue(`${status}Data`, mergedData);
		},
		[formik, getCurrentStatusData]
	);

	// Helper function to handle debounced field updates
	const handleDebouncedFieldUpdate = useCallback(
		(fieldPath: string, value: string) => {
			const currentStatus = formik.values.selectedStatus;
			if (currentStatus) {
				const currentData = getCurrentStatusData(currentStatus);
				const updatedData = {
					...currentData,
					[fieldPath]: value
				};
				updateStatusData(currentStatus, updatedData);
			}
		},
		[formik.values.selectedStatus, getCurrentStatusData, updateStatusData]
	);

	// Helper function to clear existing timeout
	const clearExistingTimeout = useCallback(
		(timeouts: Map<string, NodeJS.Timeout>, fieldPath: string) => {
			const existingTimeout = timeouts.get(fieldPath);
			if (existingTimeout) {
				clearTimeout(existingTimeout);
			}
		},
		[]
	);

	// Helper function to set new timeout
	const setNewTimeout = useCallback(
		(timeouts: Map<string, NodeJS.Timeout>, fieldPath: string, value: string) => {
			const newTimeout = setTimeout(() => {
				handleDebouncedFieldUpdate(fieldPath, value);
			}, 300);
			timeouts.set(fieldPath, newTimeout);
		},
		[handleDebouncedFieldUpdate]
	);

	// Debounced update function to prevent lag on text input
	const getDebouncedUpdate = useMemo(() => {
		const timeouts = new Map<string, NodeJS.Timeout>();

		const createDebouncedUpdater = (fieldPath: string) => (value: string) => {
			clearExistingTimeout(timeouts, fieldPath);
			setNewTimeout(timeouts, fieldPath, value);
		};

		return createDebouncedUpdater;
	}, [clearExistingTimeout, setNewTimeout]);

	// Helper function to find address component by types
	const findAddressComponentByTypes = useCallback(
		(
			addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
			types: string[]
		) => {
			return addressComponents?.find(component =>
				types.some(type => component.types.includes(type))
			);
		},
		[]
	);

	// Helper function to convert Google Places PlaceResult to AddressViewModel
	const convertPlaceResultToAddressViewModel = useCallback(
		(placeResult: google.maps.places.PlaceResult): AddressViewModel => {
			const getAddressComponent = (types: string[]) => {
				return findAddressComponentByTypes(placeResult.address_components, types);
			};

			const streetNumber = getAddressComponent(["street_number"])?.long_name || "";
			const street = getAddressComponent(["route"])?.long_name || "";
			const city =
				getAddressComponent(["locality", "administrative_area_level_1"])?.long_name || "";
			const region =
				getAddressComponent(["administrative_area_level_1"])?.long_name || "";
			const country = getAddressComponent(["country"])?.long_name || "";
			const countryCode = getAddressComponent(["country"])?.short_name || "";
			const postalCode = getAddressComponent(["postal_code"])?.long_name || "";

			const currentStatus = formik.values.selectedStatus;
			const currentData = currentStatus ? getCurrentStatusData(currentStatus) : null;

			return {
				id: currentData?.selectedAddress?.id ?? 0,
				fullAddress: placeResult.formatted_address || "",
				street,
				streetNumber,
				postalCode,
				city,
				region,
				country,
				countryCode,
				latitude: placeResult.geometry?.location?.lat() || undefined,
				longitude: placeResult.geometry?.location?.lng() || undefined,
				placeId: placeResult.place_id || undefined,
				formattedAddress: placeResult.formatted_address || undefined
			};
		},
		[formik.values.selectedStatus, getCurrentStatusData, findAddressComponentByTypes]
	);

	// Helper function to handle status change
	const handleStatusChange = useCallback(
		(newStatus: "portage" | "structure" | null) => {
			formik.setFieldValue("selectedStatus", newStatus);
		},
		[formik]
	);

	const showErrorDialog = useCallback((title: string, message: string) => {
		setErrorDialog({
			open: true,
			title,
			message
		});
	}, []);

	// Company URL management functions
	const handleAddNewCompanyUrl = useCallback(() => {
		if ((formik.values.structureData?.companyUrls || []).length >= 5) {
			showErrorDialog(
				t("common.urlsMaxCountTitle"),
				t("freelancerOnboardingMyProfessionalInformation.documentsAndLinks.urlsMaxCount")
			);
			return;
		}

		setActiveCompanyUrlTable(true);
		setIsAddingCompanyUrl(true);
		setAddingCompanyUrl({
			id: 0,
			type: null,
			url: "",
			freelancerProfileCompanyId: freelancerProfile?.company?.id
		} as FreelancerProfileCompanyUrl);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.structureData?.companyUrls, showErrorDialog, t, freelancerProfile]);

	const handleConfirmAddCompanyUrl = useCallback(() => {
		if (addingCompanyUrl?.url) {
			let normalizedUrl = normalizeUrl(addingCompanyUrl.url);

			try {
				const urlObj = new URL(normalizedUrl);
				const hostname = urlObj.hostname;
				const domainRegex =
					/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

				if (!domainRegex.test(hostname)) {
					throw new Error("Invalid domain");
				}

				const invalidDomains = ["test", "example", "placeholder", "demo", "temp"];
				if (invalidDomains.includes(hostname.toLowerCase())) {
					throw new Error("Invalid domain");
				}
			} catch {
				showErrorDialog(t("common.invalidUrlFormatTitle"), t("common.invalidUrlFormat"));
				return;
			}

			const currentCompanyUrls =
				getCurrentStatusData(formik.values.selectedStatus!)?.companyUrls || [];
			const normalizedUrls = [...currentCompanyUrls, addingCompanyUrl]
				.map(urlItem => {
					if (!urlItem?.url) return "";
					return normalizeUrl(urlItem.url).replace("www.", "").toLowerCase();
				})
				.filter(Boolean);

			const uniqueUrls = new Set(normalizedUrls);
			const isDuplicateUrl = normalizedUrls.length !== uniqueUrls.size;

			if (isDuplicateUrl) {
				showErrorDialog(t("common.urlDuplicateTitle"), t("common.urlDuplicate"));
				return;
			}

			const newCompanyUrl = {
				id: addingCompanyUrl.id,
				url: normalizedUrl,
				type: addingCompanyUrl.type
					? { id: addingCompanyUrl.type.id, label: addingCompanyUrl.type.label }
					: null,
				freelancerProfileCompanyId: freelancerProfile?.company?.id
			};
			formik.setFieldValue("structureData.companyUrls", [
				...currentCompanyUrls,
				newCompanyUrl
			]);
			setIsAddingCompanyUrl(false);
			setAddingCompanyUrl(null);
		}
	}, [
		addingCompanyUrl,
		showErrorDialog,
		t,
		formik,
		getCurrentStatusData,
		freelancerProfile
	]);

	const handleCancelAddCompanyUrl = useCallback(() => {
		setAddingCompanyUrl(null);
		setIsAddingCompanyUrl(false);
	}, []);

	const removeCompanyUrl = useCallback(
		(urlIndex: number) => {
			const currentCompanyUrls =
				getCurrentStatusData(formik.values.selectedStatus!)?.companyUrls || [];
			const updatedUrls = currentCompanyUrls.filter((_, i) => i !== urlIndex);

			// Update the status-specific data
			updateStatusData(formik.values.selectedStatus!, {
				companyUrls: updatedUrls
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formik, updateStatusData, getCurrentStatusData]
	);

	// Function to normalize URLs
	const normalizeUrl = (url: string): string => {
		let normalizedUrl = url.trim();
		if (normalizedUrl.startsWith("www.") || !/^https?:\/\//i.test(normalizedUrl)) {
			normalizedUrl = `https://${normalizedUrl}`;
		}
		return normalizedUrl;
	};

	// Document management functions
	const handleAddNewDocument = useCallback(() => {
		if ((formik.values.documents || []).length >= 5) {
			showErrorDialog(
				t("common.documentsMaxCountTitle"),
				t(
					"freelancerOnboardingMyProfessionalInformation.documentsAndLinks.documentsMaxCount"
				)
			);
			return;
		}
		setActiveTable("documents");
		setIsAddingDocument(true);
		setAddingDocument({
			id: 0,
			type: null,
			name: "",
			urlPath: "",
			content: "",
			freelancerProfileId: freelancerProfile?.id ?? 0
		} as FreelancerProfileDocument);
	}, [formik.values.documents, showErrorDialog, t, freelancerProfile]);

	const handleFileUpload = async (file: File) => {
		try {
			if (file.size > 5 * 1024 * 1024) {
				showErrorDialog(t("common.documentMaxSizeTitle"), t("common.documentMaxSize"));
				return;
			}

			const allowedTypes = [
				"application/pdf",
				"application/msword",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"application/vnd.ms-excel",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"image/jpeg",
				"image/jpg",
				"image/png"
			];
			if (!allowedTypes.includes(file.type)) {
				showErrorDialog(
					t("common.documentInvalidTypeTitle"),
					t("common.documentInvalidType")
				);
				return;
			}

			if ((formik.values.documents || []).length >= 5) {
				showErrorDialog(
					t("common.documentsMaxCountTitle"),
					t(
						"freelancerOnboardingMyProfessionalInformation.documentsAndLinks.documentsMaxCount"
					)
				);
				return;
			}

			const currentDocuments = formik.values.documents || [];
			const isDuplicateName = currentDocuments.some(
				(doc: any) => doc.name?.toLowerCase() === file.name.toLowerCase()
			);

			if (isDuplicateName) {
				showErrorDialog(
					t("common.documentDuplicateTitle"),
					t("common.documentDuplicate")
				);
				return;
			}

			const base64Content = await convertFileToBase64(file);
			setAddingDocument(
				prev =>
					({
						...prev,
						name: file.name,
						urlPath: URL.createObjectURL(file),
						content: base64Content
					} as FreelancerProfileDocument)
			);
		} catch (error) {
			console.error("Error converting file to base64:", error);
		}
	};

	const handleConfirmAddDocument = useCallback(() => {
		if (addingDocument?.name) {
			const currentDocuments = formik.values.documents || [];
			const newDocument = {
				id: addingDocument.id,
				name: addingDocument.name,
				urlPath: addingDocument.urlPath,
				type: addingDocument.type
					? { id: addingDocument.type.id, label: addingDocument.type.label }
					: null,
				content: addingDocument.content,
				freelancerProfileId: freelancerProfile?.id ?? 0
			};
			formik.setFieldValue("documents", [...currentDocuments, newDocument]);
			setAddingDocument(null);
			setIsAddingDocument(false);
		}
	}, [addingDocument, formik, freelancerProfile]);

	const handleCancelAddDocument = useCallback(() => {
		setAddingDocument(null);
		setIsAddingDocument(false);
	}, []);

	const removeDocument = useCallback(
		(documentIndex: number) => {
			const currentDocuments = formik.values.documents || [];
			formik.setFieldValue(
				"documents",
				currentDocuments.filter((_, i) => i !== documentIndex)
			);
		},
		[formik]
	);

	// URL management functions
	const handleAddNewUrl = useCallback(() => {
		if ((formik.values.urls || []).length >= 5) {
			showErrorDialog(
				t("common.urlsMaxCountTitle"),
				t("freelancerOnboardingMyProfessionalInformation.documentsAndLinks.urlsMaxCount")
			);
			return;
		}
		setActiveTable("urls");
		setIsAddingUrl(true);
		setAddingUrl({
			id: 0,
			type: null,
			url: "",
			freelancerProfileId: freelancerProfile?.id
		} as FreelancerProfileUrl);
	}, [formik.values.urls, showErrorDialog, t, freelancerProfile]);

	const handleConfirmAddUrl = useCallback(() => {
		if (addingUrl?.url) {
			let normalizedUrl = normalizeUrl(addingUrl.url);

			try {
				const urlObj = new URL(normalizedUrl);
				const hostname = urlObj.hostname;
				const domainRegex =
					/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

				if (!domainRegex.test(hostname)) {
					throw new Error("Invalid domain");
				}

				const invalidDomains = ["test", "example", "placeholder", "demo", "temp"];
				if (invalidDomains.includes(hostname.toLowerCase())) {
					throw new Error("Invalid domain");
				}
			} catch {
				showErrorDialog(t("common.invalidUrlFormatTitle"), t("common.invalidUrlFormat"));
				return;
			}

			const currentUrls = formik.values.urls || [];
			const normalizedUrls = [...currentUrls, addingUrl]
				.map(urlItem => {
					if (!urlItem?.url) return "";
					return normalizeUrl(urlItem.url).replace("www.", "").toLowerCase();
				})
				.filter(Boolean);

			const uniqueUrls = new Set(normalizedUrls);
			const isDuplicateUrl = normalizedUrls.length !== uniqueUrls.size;

			if (isDuplicateUrl) {
				showErrorDialog(t("common.urlDuplicateTitle"), t("common.urlDuplicate"));
				return;
			}

			const newUrl = {
				id: addingUrl.id,
				url: normalizedUrl,
				type: addingUrl.type
					? { id: addingUrl.type.id, label: addingUrl.type.label }
					: null,
				freelancerProfileId: freelancerProfile?.id ?? 0
			};
			formik.setFieldValue("urls", [...currentUrls, newUrl]);
			setIsAddingUrl(false);
			setAddingUrl(null);
		}
	}, [addingUrl, showErrorDialog, t, formik, freelancerProfile]);

	const handleCancelAddUrl = useCallback(() => {
		setAddingUrl(null);
		setIsAddingUrl(false);
	}, []);

	const removeUrl = useCallback(
		(urlIndex: number) => {
			const currentUrls = formik.values.urls || [];
			formik.setFieldValue(
				"urls",
				currentUrls.filter((_, i) => i !== urlIndex)
			);
		},
		[formik]
	);

	return (
		<form onSubmit={handleFormSubmit}>
			<Card
				component={Paper}
				elevation={0}
				sx={{
					marginX: "32px",
					marginY: "48px"
				}}
				data-testid="professional-information"
			>
				<CardContent sx={{ p: 4 }}>
					<Typography variant="h2" component="h1" sx={{ mb: 1 }}>
						{t("freelancerOnboardingMyProfessionalInformation.title")}
					</Typography>
					<Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
						{t("freelancerOnboardingMyProfessionalInformation.subtitle")}
					</Typography>

					{/* Daily Rate Section */}
					<DailyRateSection formik={formik} sliderRef={dailyRateRef} />

					{/* Mission Duration and Location Section */}
					<MissionPreferencesSection
						formik={formik}
						missionsDurations={missionsDurations}
						jobLocations={jobLocations}
						missionDurationRef={missionDurationRef}
						locationPreferenceRef={locationPreferenceRef}
					/>

					{/* Documents and URLs Section */}
					<DocumentsAndUrlsSection
						formik={formik}
						activeTable={activeTable}
						setActiveTable={setActiveTable}
						isAddingDocument={isAddingDocument}
						isAddingUrl={isAddingUrl}
						addingDocument={addingDocument}
						addingUrl={addingUrl}
						setAddingDocument={setAddingDocument}
						setAddingUrl={setAddingUrl}
						documentTypes={documentTypes as { id: number; label: string }[]}
						urlTypes={urlTypes as { id: number; label: string }[]}
						handleAddNewDocument={handleAddNewDocument}
						handleAddNewUrl={handleAddNewUrl}
						handleFileUpload={handleFileUpload}
						handleConfirmAddDocument={handleConfirmAddDocument}
						handleCancelAddDocument={handleCancelAddDocument}
						handleConfirmAddUrl={handleConfirmAddUrl}
						handleCancelAddUrl={handleCancelAddUrl}
						removeDocument={removeDocument}
						removeUrl={removeUrl}
					/>

					{/* Additional Buttons Section */}
					{/* Status Selection Section */}
					<StatusSelectionSection
						formik={formik}
						onStatusChange={handleStatusChange}
						buttonRef={statusSelectionRef}
					/>

					{/* Status-specific form fields */}
					{formik.values.selectedStatus && (
						<CompanyInfoSection
							formik={formik}
							selectedStatus={formik.values.selectedStatus}
							getCurrentStatusData={getCurrentStatusData}
							updateStatusData={updateStatusData}
							getDebouncedUpdate={getDebouncedUpdate}
							convertPlaceResultToAddressViewModel={convertPlaceResultToAddressViewModel}
							activeCompanyUrlTable={activeCompanyUrlTable}
							setActiveCompanyUrlTable={setActiveCompanyUrlTable}
							isAddingCompanyUrl={isAddingCompanyUrl}
							addingCompanyUrl={addingCompanyUrl}
							setAddingCompanyUrl={setAddingCompanyUrl}
							urlTypes={urlTypes as { id: number; label: string }[]}
							handleAddNewCompanyUrl={handleAddNewCompanyUrl}
							handleConfirmAddCompanyUrl={handleConfirmAddCompanyUrl}
							handleCancelAddCompanyUrl={handleCancelAddCompanyUrl}
							removeCompanyUrl={removeCompanyUrl}
							setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated={
								setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated
							}
							fieldRef={companyInfoRef}
						/>
					)}

					<ErrorDialog
						open={errorDialog.open}
						title={errorDialog.title}
						message={errorDialog.message}
						onClose={() => setErrorDialog({ ...errorDialog, open: false })}
					/>

					{/* Action Buttons */}
					<ActionButtonsSection
						freelancerProfile={freelancerProfile}
						isSubmitting={formik.isSubmitting}
						onSaveProgress={async () => {
							interceptNavigation({
								onValidNavigation: async () => {
									try {
										await saveMyProfessionalInformationFreelancerOnboardingProgress.mutateAsync(
											{
												freelancerOnboardingMyProfessionalInformationStepViewModel:
													formik.values,
												freelancerProfile: freelancerProfile!,
												newStep: FreelancerOnboardingFormStep.MyAvailability
											}
										);

										setCurrentOnboardingFormStep(
											FreelancerOnboardingFormStep.MyAvailability
										);
									} catch (error) {
										console.error("Error returning back to the previous step:", error);
									}
								},
								onBlockedNavigation: () => {
									setNextOnboardingStep(FreelancerOnboardingFormStep.MyAvailability);
								}
							});
						}}
					/>
				</CardContent>
			</Card>
		</form>
	);
};

export default FreelancerOnboardingMyProfessionalInformation;
